import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableChip } from '../../components/PremiumDesign';
import {
  encodeQuotationId,
  formatQuoteDate,
  formatQuoteMoney,
  CUSTOMER_QUOTE_STATUS_LABEL,
  CUSTOMER_QUOTE_STATUS_TONE,
  showDeliveryLine,
} from '../../quotations/utils';
import { env } from '@/common/config';

function resolvePdfUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = (env.photoBaseUrl || '').replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

function sumQuoteTotals(quotations) {
  return (quotations || []).reduce((sum, q) => {
    const n = parseFloat(q?.total_amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function dominantCurrency(quotations) {
  const first = (quotations || []).find((q) => q?.currency)?.currency;
  return first || 'INR';
}

export function ContactQuotationsList({
  quotations = [],
  title = 'Quotations',
  emptyMessage = 'No quotations found.',
  showSummary = true,
}) {
  const navigate = useNavigate();
  const list = useMemo(
    () => (Array.isArray(quotations) ? quotations : []),
    [quotations],
  );

  const totalValue = useMemo(() => sumQuoteTotals(list), [list]);
  const currency = useMemo(() => dominantCurrency(list), [list]);

  const openQuote = (cq) => {
    if (!cq?.id) return;
    if (cq.customer_name) {
      try {
        sessionStorage.setItem('quotation-customer-name', cq.customer_name);
        window.dispatchEvent(new Event('quotation-customer-name-updated'));
      } catch {
        // ignore
      }
    }
    navigate(`/quotations/customer/${encodeQuotationId(cq.id)}`, {
      state: { customerName: cq.customer_name },
    });
  };

  const openPdf = (e, url) => {
    e.stopPropagation();
    if (!url) return;
    window.open(resolvePdfUrl(url), '_blank', 'noopener,noreferrer');
  };

  const getCustomTone = (statusKey) => {
    const s = String(statusKey || '').toLowerCase();
    if (s === 'generated') return 'success';
    if (s === 'sent' || s === 'sent_whatsapp') return 'info';
    if (s === 'expired') return 'error';
    if (s === 'draft') return 'neutral';
    return CUSTOMER_QUOTE_STATUS_TONE[statusKey] || 'neutral';
  };

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-bold text-[#1F2241]">
          {title}
          {list.length > 0 ? (
            <span className="ml-1.5 text-[#868686] font-medium normal-case tracking-normal text-sm">
              ({list.length})
            </span>
          ) : null}
        </span>
      </div>

      {showSummary && list.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-[12px] border border-[#EBF4EA] bg-[#F5F5F5] px-4 py-3">
          <span className="text-xs text-[#868686] font-medium">
            Total value generated
          </span>
          <span className="text-sm font-bold text-[#1F2241]">
            {formatQuoteMoney(totalValue, currency)}
          </span>
        </div>
      )}

      {list.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center border-2 border-dashed border-[#EBF4EA] rounded-[24px] bg-white py-16 px-6">
          <div className="w-14 h-14 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4">
            <svg
              className="w-7 h-7 text-[#868686]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h4 className="text-base font-bold text-[#1F2241] mb-1.5">
            No quotations available
          </h4>
          <p className="text-sm text-[#868686] max-w-[280px]">{emptyMessage}</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {list.map((cq) => {
            const status = String(cq.status || '').toLowerCase();
            const hasPdf = Boolean(cq.pdf_url);

            return (
              <li key={cq.id} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => openQuote(cq)}
                  className="group w-full text-left bg-white border border-[#EBF4EA] rounded-[20px] hover:border-primary/50 hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.1)] transition-all duration-300 flex flex-col h-full p-4 md:p-5"
                >
                  <div className="flex flex-col gap-4 h-full w-full">
                    {/* Top: Number & Status */}
                    <div className="flex items-start justify-between gap-3 w-full">
                      <span className="text-[11px] font-mono font-bold text-primary truncate bg-primary/5 px-2 py-1 rounded-md border border-primary/10">
                        {cq.quote_number || `Quote #${cq.id}`}
                      </span>
                      <TableChip
                        tone={getCustomTone(status)}
                        className="shrink-0"
                      >
                        {CUSTOMER_QUOTE_STATUS_LABEL[status] ||
                          cq.status ||
                          '—'}
                      </TableChip>
                    </div>

                    {/* Middle: Title, Date, Items */}
                    <div className="flex flex-col gap-1.5 w-full">
                      <span className="text-[15px] font-extrabold text-[#1F2241] line-clamp-2 leading-snug">
                        {cq.quotation_title || 'Quotation'}
                      </span>
                      <span className="text-[12px] font-medium text-[#868686] flex items-center gap-1.5">
                        <svg
                          className="w-3.5 h-3.5 opacity-70"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {formatQuoteDate(cq.created_at)}
                        {Array.isArray(cq.items) && cq.items.length > 0 && (
                          <>
                            <span>•</span>
                            <span>
                              {cq.items.length} item
                              {cq.items.length === 1 ? '' : 's'}
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    {/* Bottom: Total Amount & Actions */}
                    <div className="mt-auto pt-4 border-t border-[#EBF4EA] w-full flex flex-col gap-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-[#868686] font-bold uppercase tracking-wider">
                          Total Amount
                        </span>
                        <span className="text-xl font-black text-primary">
                          {formatQuoteMoney(cq.total_amount, cq.currency)}
                        </span>
                        {showDeliveryLine(
                          cq.delivery_term,
                          cq.delivery_charge,
                        ) && (
                          <span className="text-[11px] text-[#868686]">
                            Includes {cq.delivery_term || 'Delivery'}:{' '}
                            {formatQuoteMoney(cq.delivery_charge, cq.currency)}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row items-center gap-2 w-full mt-1">
                        {hasPdf && (
                          <a
                            href={resolvePdfUrl(cq.pdf_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openPdf(e, cq.pdf_url)}
                            className="flex-1 flex justify-center items-center gap-2 px-3 min-h-[44px] rounded-xl border border-border-default bg-[#F5F5F5] text-xs font-bold text-[#1F2241] hover:bg-[#EBF4EA] hover:border-primary/40 transition-colors w-full"
                          >
                            <svg
                              className="w-4 h-4 text-primary"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            View PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
